import { useState, useCallback, useEffect } from 'react';
import { Pipette, RotateCcw, Copy, Check, Palette } from 'lucide-react';

interface ColorScale {
  '50': string;
  '100': string;
  '200': string;
  '300': string;
  '400': string;
  '500': string;
  '600': string;
  '700': string;
  '800': string;
  '900': string;
  '950': string;
}

interface ColorPickerProps {
  label: string;
  value: ColorScale;
  onChange: (colors: ColorScale) => void;
  presets?: { name: string; colors: ColorScale }[];
}

// Converte RGB string "R G B" para hex
function rgbToHex(rgb: string): string {
  const [r, g, b] = rgb.split(' ').map(Number);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Converte hex para RGB string "R G B"
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0 0 0';
  return `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`;
}

// Gera uma escala de cores a partir de uma cor base (500)
function generateColorScale(baseHex: string): ColorScale {
  const [r, g, b] = hexToRgb(baseHex).split(' ').map(Number);
  
  // Função para ajustar luminosidade
  const adjustBrightness = (r: number, g: number, b: number, factor: number) => {
    if (factor > 1) {
      // Clarear
      return [
        Math.round(r + (255 - r) * (factor - 1)),
        Math.round(g + (255 - g) * (factor - 1)),
        Math.round(b + (255 - b) * (factor - 1)),
      ];
    } else {
      // Escurecer
      return [
        Math.round(r * factor),
        Math.round(g * factor),
        Math.round(b * factor),
      ];
    }
  };

  const shades: Record<string, number> = {
    '50': 1.9,
    '100': 1.7,
    '200': 1.5,
    '300': 1.3,
    '400': 1.15,
    '500': 1,
    '600': 0.85,
    '700': 0.7,
    '800': 0.55,
    '900': 0.4,
    '950': 0.25,
  };

  const scale: Partial<ColorScale> = {};
  
  Object.entries(shades).forEach(([shade, factor]) => {
    const [nr, ng, nb] = adjustBrightness(r, g, b, factor);
    scale[shade as keyof ColorScale] = `${nr} ${ng} ${nb}`;
  });

  return scale as ColorScale;
}

// Presets de cores populares
const DEFAULT_PRESETS: { name: string; colors: ColorScale }[] = [
  {
    name: 'Cyan',
    colors: generateColorScale('#06b6d4'),
  },
  {
    name: 'Blue',
    colors: generateColorScale('#3b82f6'),
  },
  {
    name: 'Indigo',
    colors: generateColorScale('#6366f1'),
  },
  {
    name: 'Purple',
    colors: generateColorScale('#a855f7'),
  },
  {
    name: 'Pink',
    colors: generateColorScale('#ec4899'),
  },
  {
    name: 'Red',
    colors: generateColorScale('#ef4444'),
  },
  {
    name: 'Orange',
    colors: generateColorScale('#f97316'),
  },
  {
    name: 'Amber',
    colors: generateColorScale('#f59e0b'),
  },
  {
    name: 'Green',
    colors: generateColorScale('#22c55e'),
  },
  {
    name: 'Emerald',
    colors: generateColorScale('#10b981'),
  },
  {
    name: 'Teal',
    colors: generateColorScale('#14b8a6'),
  },
  {
    name: 'Slate',
    colors: generateColorScale('#64748b'),
  },
];

export function ColorPicker({ label, value, onChange, presets = DEFAULT_PRESETS }: ColorPickerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [customColor, setCustomColor] = useState(rgbToHex(value['500']));
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    setCustomColor(rgbToHex(value['500']));
  }, [value]);

  const handleCustomColorChange = useCallback((hex: string) => {
    setCustomColor(hex);
    const newScale = generateColorScale(hex);
    onChange(newScale);
  }, [onChange]);

  const handlePresetSelect = useCallback((preset: { name: string; colors: ColorScale }) => {
    onChange(preset.colors);
    setCustomColor(rgbToHex(preset.colors['500']));
  }, [onChange]);

  const copyToClipboard = useCallback((shade: string, rgb: string) => {
    navigator.clipboard.writeText(rgb);
    setCopied(shade);
    setTimeout(() => setCopied(null), 1500);
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-surface-300">{label}</label>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1"
        >
          <Palette className="w-3 h-3" />
          {isExpanded ? 'Fechar' : 'Personalizar'}
        </button>
      </div>

      {/* Preview da escala atual */}
      <div className="flex gap-0.5 rounded-lg overflow-hidden">
        {(['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const).map((shade) => (
          <div
            key={shade}
            className="flex-1 h-8 cursor-pointer hover:scale-y-110 transition-transform relative group"
            style={{ backgroundColor: `rgb(${value[shade]})` }}
            onClick={() => copyToClipboard(shade, value[shade])}
            title={`${shade}: rgb(${value[shade]})`}
          >
            {copied === shade && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 text-center text-[8px] text-white/70 opacity-0 group-hover:opacity-100 transition-opacity">
              {shade}
            </div>
          </div>
        ))}
      </div>

      {/* Painel expandido */}
      {isExpanded && (
        <div className="bg-surface-800 rounded-lg p-4 space-y-4 border border-surface-700">
          {/* Color picker nativo */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="color"
                value={customColor}
                onChange={(e) => handleCustomColorChange(e.target.value)}
                className="w-12 h-12 rounded-lg cursor-pointer border-2 border-surface-600"
              />
              <Pipette className="absolute bottom-1 right-1 w-3 h-3 text-white pointer-events-none" />
            </div>
            <div className="flex-1">
              <label className="text-xs text-surface-400 block mb-1">Cor Base (500)</label>
              <input
                type="text"
                value={customColor}
                onChange={(e) => {
                  if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                    handleCustomColorChange(e.target.value);
                  }
                  setCustomColor(e.target.value);
                }}
                className="w-full px-3 py-1.5 bg-surface-700 border border-surface-600 rounded text-white text-sm font-mono"
                placeholder="#000000"
              />
            </div>
          </div>

          {/* Presets */}
          <div>
            <label className="text-xs text-surface-400 block mb-2">Presets Rápidos</label>
            <div className="grid grid-cols-6 gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => handlePresetSelect(preset)}
                  className="group relative"
                  title={preset.name}
                >
                  <div
                    className="w-full h-8 rounded-lg border-2 border-transparent hover:border-white/30 transition-all"
                    style={{ backgroundColor: `rgb(${preset.colors['500']})` }}
                  />
                  <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[10px] text-surface-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {preset.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Escala detalhada */}
          <div>
            <label className="text-xs text-surface-400 block mb-2">Escala Completa</label>
            <div className="grid grid-cols-11 gap-1">
              {(['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const).map((shade) => (
                <div key={shade} className="text-center">
                  <div
                    className="w-full aspect-square rounded cursor-pointer hover:ring-2 hover:ring-white/30 transition-all"
                    style={{ backgroundColor: `rgb(${value[shade]})` }}
                    onClick={() => copyToClipboard(shade, value[shade])}
                  />
                  <span className="text-[10px] text-surface-500 mt-1 block">{shade}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ColorPicker;
